import { CategoryPicker } from '@/components/categories/CategoryPicker';
import { ActionType, AppRuleAction } from '@polybank/shared';
import { Folder, Plus, Sparkles, Store, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCallback } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { RuleFormValues } from '@/components/rules/form/rule-form-types';

interface RuleActionBuilderProps {
  isMobile?: boolean;
}

const actionTypes = [
  { value: 'set_category', label: 'Set Category', icon: Folder },
  { value: 'set_merchant', label: 'Set Merchant', icon: Store },
  { value: 'add_tags', label: 'Add Tags', icon: Tag },
];

export function RuleActionBuilder({
  isMobile = false,
}: RuleActionBuilderProps) {
  const { control, watch } = useFormContext<RuleFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'actions',
  });

  const addAction = useCallback(() => {
    append({
      actionType: ActionType.SET_CATEGORY,
      actionValue: '',
    } as AppRuleAction);
  }, [append]);

  const removeAction = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const getInputPlaceholder = useCallback((type: string) => {
    if (type === 'set_category') {
      return 'Select or type a category';
    }
    if (type === 'set_merchant') {
      return 'Enter merchant name';
    }
    if (type === 'add_tags') {
      return 'Enter tags separated by commas';
    }
    return '';
  }, []);

  // Mobile layout
  if (isMobile) {
    return (
      <div className='space-y-4'>
        {fields.length > 0 && (
          <Button
            type='button'
            variant='outline'
            onClick={addAction}
            className='w-full gap-2 h-11 border-dashed border-2 hover:bg-slate-50'
          >
            <Plus className='w-4 h-4' />
            Add Action
          </Button>
        )}

        {fields.length === 0 ? (
          <Card
            role='button'
            tabIndex={0}
            className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
            onClick={addAction}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                addAction();
              }
            }}
          >
            <CardContent className='flex flex-col items-center justify-center py-8 text-center'>
              <div className='w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-purple-500/10 to-pink-600/10 flex items-center justify-center'>
                <Sparkles className='w-6 h-6 text-purple-600' />
              </div>
              <p className='font-medium text-slate-900 mb-1'>Add an action</p>
              <p className='text-sm text-slate-500'>
                Define what should happen when this rule triggers
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {fields.map((field, index) => {
              return (
                <Card key={field.id} className='relative'>
                  <CardContent className='p-4'>
                    <div className='flex items-start justify-between gap-3'>
                      <div className='flex-1 space-y-3'>
                        <div className='flex items-center gap-2'>
                          {index === 0 && (
                            <div className='text-sm font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded'>
                              Then
                            </div>
                          )}
                          {index > 0 && (
                            <div className='text-sm font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded'>
                              AND
                            </div>
                          )}
                        </div>

                        <div className='grid grid-cols-1 gap-3'>
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium text-slate-700'>
                              Action Type
                            </Label>
                            <Controller
                              name={`actions.${index}.actionType`}
                              control={control}
                              render={({ field: controlField }) => (
                                <Select
                                  value={controlField.value}
                                  onValueChange={controlField.onChange}
                                >
                                  <SelectTrigger className='h-10'>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {actionTypes.map((type) => {
                                      const Icon = type.icon;
                                      return (
                                        <SelectItem
                                          key={type.value}
                                          value={type.value}
                                        >
                                          <div className='flex items-center gap-2'>
                                            <Icon className='w-4 h-4' />
                                            {type.label}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </div>

                          <div className='space-y-2'>
                            <Label className='text-sm font-medium text-slate-700'>
                              Value
                            </Label>
                            <Controller
                              name={`actions.${index}.actionValue`}
                              control={control}
                              render={({ field: controlField }) => {
                                const actionType = watch(
                                  `actions.${index}.actionType`
                                );
                                if (actionType === ActionType.SET_CATEGORY) {
                                  return (
                                    <CategoryPicker
                                      currentCategoryId={controlField.value}
                                      onCategoryChange={(category) =>
                                        controlField.onChange(
                                          category?.$id || ''
                                        )
                                      }
                                    />
                                  );
                                }

                                return (
                                  <Input
                                    {...controlField}
                                    placeholder={getInputPlaceholder(
                                      actionType
                                    )}
                                  />
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        onClick={() => removeAction(index)}
                        className='text-slate-400 hover:text-red-500 hover:bg-red-50 mt-1'
                      >
                        <X className='w-4 h-4' />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Empty card for adding new action */}
            <Card
              role='button'
              tabIndex={0}
              className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
              onClick={addAction}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  addAction();
                }
              }}
            >
              <CardContent className='flex flex-col items-center justify-center py-6 text-center'>
                <div className='w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br from-slate-500/10 to-slate-700/10 flex items-center justify-center'>
                  <Plus className='w-5 h-5 text-slate-600' />
                </div>
                <p className='font-medium text-slate-900 text-sm'>
                  Add another action
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // Desktop layout (improved)
  return (
    <div className='space-y-6'>
      {fields.length > 0 && (
        <Button
          type='button'
          variant='outline'
          onClick={addAction}
          className='w-full gap-2 h-11 border-dashed border-2 hover:bg-slate-50'
        >
          <Plus className='w-4 h-4' />
          Add Action
        </Button>
      )}

      {fields.length === 0 ? (
        <Card
          className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
          onClick={addAction}
        >
          <CardContent className='flex flex-col items-center justify-center py-12 text-center'>
            <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500/10 to-pink-600/10 flex items-center justify-center'>
              <Sparkles className='w-8 h-8 text-purple-600' />
            </div>
            <p className='font-medium text-slate-900 mb-2 text-lg'>
              {fields.length > 0 ? 'Add another action' : 'Add an action'}
            </p>
            <p className='text-sm text-slate-500 max-w-sm'>
              Define what should happen when this rule triggers
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {fields.map((field, index) => {
            return (
              <Card key={field.id} className='relative'>
                <CardContent className='p-6'>
                  <div className='flex items-start justify-between gap-4'>
                    <div className='flex-1 space-y-4'>
                      <div className='flex items-center gap-3'>
                        <div className='flex-1 items-center justify-items-start'>
                          {index === 0 && (
                            <div className='text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1.5 rounded-full'>
                              Then
                            </div>
                          )}
                          {index > 0 && (
                            <div className='text-sm font-medium text-pink-600 bg-pink-50 px-3 py-1.5 rounded-full'>
                              AND
                            </div>
                          )}
                        </div>
                      </div>

                      <div className='grid grid-cols-1'>
                        <div className='space-y-2'>
                          <Label className='text-sm font-medium text-slate-700'>
                            Action Type
                          </Label>
                          <Controller
                            name={`actions.${index}.actionType`}
                            control={control}
                            render={({ field: controlField }) => (
                              <Select
                                value={controlField.value}
                                onValueChange={controlField.onChange}
                              >
                                <SelectTrigger className='h-11 w-full'>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {actionTypes.map((type) => {
                                    const Icon = type.icon;
                                    return (
                                      <SelectItem
                                        key={type.value}
                                        value={type.value}
                                      >
                                        <div className='flex items-center gap-2'>
                                          <Icon className='w-4 h-4' />
                                          {type.label}
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        <div className='space-y-2'>
                          <Label className='text-sm font-medium text-slate-700'>
                            Value
                          </Label>
                          <Controller
                            name={`actions.${index}.actionValue`}
                            control={control}
                            render={({ field: controlField }) => {
                              const actionType = watch(
                                `actions.${index}.actionType`
                              );
                              if (actionType === ActionType.SET_CATEGORY) {
                                return (
                                  <CategoryPicker
                                    currentCategoryId={controlField.value}
                                    onCategoryChange={(category) =>
                                      controlField.onChange(category?.$id || '')
                                    }
                                  />
                                );
                              }

                              return (
                                <Input
                                  {...controlField}
                                  placeholder={getInputPlaceholder(actionType)}
                                />
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={() => removeAction(index)}
                      className='text-slate-400 hover:text-red-500 hover:bg-red-50 mt-2'
                    >
                      <X className='w-5 h-5' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Empty card for adding new action */}
          <Card
            role='button'
            tabIndex={0}
            className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
            onClick={addAction}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                addAction();
              }
            }}
          >
            <CardContent className='flex flex-col items-center justify-center py-8 text-center'>
              <div className='w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-slate-500/10 to-slate-700/10 flex items-center justify-center'>
                <Plus className='w-6 h-6 text-slate-600' />
              </div>
              <p className='font-medium text-slate-900 text-base'>
                Add another action
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
